import { useState } from "react";
import { Plus, Armchair, Trash2, UserMinus, GripVertical } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, StatCard, EmptyState, PageSkeleton } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const SHAPES = ["Round", "Rectangle", "Oval"];

export default function Seating() {
  const tables = useCollection("tables");
  const guests = useCollection("guests");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ shape: "Round", capacity: 8 });
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  if (tables.loading || guests.loading) return <PageSkeleton />;

  const assignedTo = (tableId) => guests.items.filter((g) => g.table === tableId);
  const unassigned = guests.items.filter((g) => !g.table);
  const totalSeats = tables.items.reduce((s, t) => s + Number(t.capacity || 0), 0);
  const occupied = guests.items.filter((g) => g.table).length;

  const saveTable = async () => {
    if (!form.name?.trim()) { toast.error("Name your table."); return; }
    await tables.create({ name: form.name.trim(), shape: form.shape, capacity: Number(form.capacity) || 8 });
    toast.success("Table added"); setOpen(false); setForm({ shape: "Round", capacity: 8 });
  };

  const deleteTable = async (t) => {
    for (const g of assignedTo(t.id)) await guests.update(g.id, { table: "" });
    await tables.remove(t.id); toast.success("Table removed");
  };

  const assign = async (guestId, tableId) => {
    const seated = assignedTo(tableId);
    const table = tables.items.find((t) => t.id === tableId);
    if (table && seated.length >= Number(table.capacity || 0) && !seated.some((g) => g.id === guestId)) {
      toast.error(`${table.name} is full`); return;
    }
    await guests.update(guestId, { table: tableId });
  };
  const unseat = async (guestId) => { await guests.update(guestId, { table: "" }); };

  const onDrop = (tableId) => {
    setDropTarget(null);
    if (!dragId) return;
    if (tableId === "__unassigned") unseat(dragId); else assign(dragId, tableId);
    setDragId(null);
  };

  const chip = (g, inTable) => (
    <div key={g.id} draggable
      onDragStart={() => setDragId(g.id)} onDragEnd={() => { setDragId(null); setDropTarget(null); }}
      data-testid={inTable ? "seated-guest" : "unassigned-guest"}
      className={`group flex items-center gap-2 text-sm rounded-lg bg-secondary/50 px-3 py-2 cursor-grab active:cursor-grabbing border border-transparent hover:border-accent/40 transition-colors ${dragId === g.id ? "opacity-40" : ""}`}>
      <GripVertical className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
      <span className="truncate flex-1">{g.name}</span>
      {inTable
        ? <button onClick={() => unseat(g.id)} data-testid="unseat-guest" className="text-muted-foreground hover:text-destructive"><UserMinus className="h-4 w-4" /></button>
        : (
          <Select onValueChange={(tid) => assign(g.id, tid)}>
            <SelectTrigger className="h-7 w-24 rounded-md text-xs shrink-0 sm:hidden" data-testid="assign-select"><SelectValue placeholder="Seat" /></SelectTrigger>
            <SelectContent>{tables.items.map((t) => <SelectItem key={t.id} value={t.id} disabled={assignedTo(t.id).length >= Number(t.capacity || 0)}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
    </div>
  );

  return (
    <div>
      <PageHeader title="Seating Plan" subtitle="Drag each guest to a table — or use the dropdown on mobile. Everyone gets one seat." testId="seating-header"
        action={<Button onClick={() => setOpen(true)} className="rounded-full" data-testid="add-table"><Plus className="h-4 w-4 mr-1.5" />Add Table</Button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard index={0} label="Tables" value={tables.items.length} testId="seat-tables" />
        <StatCard index={1} label="Total seats" value={totalSeats} testId="seat-total" />
        <StatCard index={2} label="Seated" value={occupied} accent testId="seat-occupied" />
        <StatCard index={3} label="Unassigned" value={unassigned.length} testId="seat-unassigned" />
      </div>

      {tables.items.length === 0 ? (
        <EmptyState icon={Armchair} title="No tables yet" description="Create your first table to start arranging your guests." actionLabel="Add Table" onAction={() => setOpen(true)} testId="seating-empty" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {tables.items.map((t) => {
              const seated = assignedTo(t.id);
              const full = seated.length >= Number(t.capacity || 0);
              const active = dropTarget === t.id;
              return (
                <div key={t.id} data-testid="table-card"
                  onDragOver={(e) => { e.preventDefault(); setDropTarget(t.id); }}
                  onDragLeave={() => setDropTarget((p) => (p === t.id ? null : p))}
                  onDrop={() => onDrop(t.id)}
                  className={`rounded-2xl bg-card border p-5 transition-all ${active ? "border-accent ring-2 ring-accent/40 scale-[1.01]" : "border-border"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.shape} · {seated.length}/{t.capacity} seats{full ? " · full" : ""}</div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteTable(t)} data-testid="delete-table"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className={`mx-auto mb-3 flex items-center justify-center bg-secondary/50 border border-border text-xs text-muted-foreground ${t.shape === "Round" ? "h-24 w-24 rounded-full" : t.shape === "Oval" ? "h-20 w-32 rounded-full" : "h-20 w-32 rounded-xl"}`}>{seated.length}/{t.capacity}</div>
                  <div className="space-y-1.5 min-h-[40px]">
                    {seated.map((g) => chip(g, true))}
                    {seated.length === 0 && <p className="text-xs text-muted-foreground text-center py-3 border border-dashed border-border rounded-lg">Drop guests here</p>}
                  </div>
                </div>
              );
            })}
          </div>

          <div data-testid="unassigned-zone"
            onDragOver={(e) => { e.preventDefault(); setDropTarget("__unassigned"); }}
            onDragLeave={() => setDropTarget((p) => (p === "__unassigned" ? null : p))}
            onDrop={() => onDrop("__unassigned")}
            className={`rounded-2xl bg-card border p-5 h-fit transition-all ${dropTarget === "__unassigned" ? "border-accent ring-2 ring-accent/40" : "border-border"}`}>
            <h3 className="text-lg tracking-tight text-primary mb-3">Unassigned guests</h3>
            {unassigned.length === 0 ? <p className="text-sm text-muted-foreground">Everyone has a seat. ✦</p> : (
              <div className="space-y-2">{unassigned.map((g) => chip(g, false))}</div>
            )}
            {guests.items.length === 0 && <p className="text-xs text-muted-foreground mt-2">Add guests in the Guest List first.</p>}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-2xl">Add table</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs text-muted-foreground">Table name</Label><Input value={form.name || ""} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="rounded-xl mt-1" data-testid="table-name" placeholder="e.g. Table 1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Shape</Label>
                <Select value={form.shape} onValueChange={(v) => setForm((p) => ({ ...p, shape: v }))}><SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger><SelectContent>{SHAPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-muted-foreground">Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => setForm((p) => ({ ...p, capacity: e.target.value }))} className="rounded-xl mt-1" data-testid="table-capacity" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button><Button className="rounded-full" onClick={saveTable} data-testid="save-table">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
